import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getEnabledMarkets, detectMarket } from '../api/real/marketApi';
import { setAnalyticsCurrency } from '../utils/analytics';

const STORAGE_KEY = 'skinnthrive_market';

const DEFAULT_MARKET = {
  code: 'IN',
  name: 'India',
  currencyCode: 'INR',
  currencySymbol: '₹',
  isDefault: true,
  codEnabled: true,
  freeShippingThreshold: 699,
  shippingBaseFee: 80,
};

const MarketContext = createContext(null);

export function MarketProvider({ children }) {
  const [currentMarket, setCurrentMarketState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : DEFAULT_MARKET;
    } catch {
      return DEFAULT_MARKET;
    }
  });

  const [availableMarkets, setAvailableMarkets] = useState([]);
  const [marketsLoaded, setMarketsLoaded] = useState(false);
  const [suggestedMarket, setSuggestedMarket] = useState(null); // non-null = show banner
  const [detectionDone, setDetectionDone] = useState(false);

  // Persist preference to localStorage whenever it changes
  const setCurrentMarket = useCallback((market) => {
    setCurrentMarketState(market);
    setAnalyticsCurrency(market?.currencyCode);
    setSuggestedMarket(null); // dismiss any banner
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(market));
    } catch { /* ignore */ }
  }, []);

  // Load enabled markets for the switcher; always refresh current market from API
  // so discount config, thresholds, and fees reflect the latest admin settings.
  useEffect(() => {
    getEnabledMarkets()
      .then(markets => {
        setAvailableMarkets(markets);
        if (markets.length === 0) return;

        const freshMarket = markets.find(m => m.code === currentMarket.code && m.isEnabled);
        if (freshMarket) {
          // Always replace stored market with fresh API data — covers both default and
          // non-default markets so discount/shipping config changes in admin take effect
          // immediately without requiring a localStorage clear.
          setCurrentMarketState(freshMarket);
          setAnalyticsCurrency(freshMarket.currencyCode);
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(freshMarket)); } catch { /* ignore */ }
        } else if (!currentMarket.isDefault) {
          // Non-default market was disabled in admin — reset to default
          const defaultMarket = markets.find(m => m.isDefault) ?? DEFAULT_MARKET;
          setCurrentMarketState(defaultMarket);
          try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        }
      })
      .catch(() => { /* non-fatal */ })
      .finally(() => setMarketsLoaded(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // IP-based market detection — skipped for ad traffic (utm_source present)
  useEffect(() => {
    if (detectionDone) return;

    const params = new URLSearchParams(window.location.search);
    const isAdTraffic = params.has('utm_source') || params.has('gclid');
    if (isAdTraffic) {
      setDetectionDone(true);
      return;
    }

    // If user has already set a preference, don't re-detect aggressively
    // (still detect to show banner if they're on the wrong one)
    detectMarket(currentMarket.code)
      .then(({ detectedMarket, shouldSuggestRedirect }) => {
        if (shouldSuggestRedirect && detectedMarket) {
          setSuggestedMarket(detectedMarket);
        }
      })
      .catch(() => { /* non-fatal */ })
      .finally(() => setDetectionDone(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissSuggestion = useCallback(() => {
    setSuggestedMarket(null);
  }, []);

  const resetToDefault = useCallback(() => {
    setCurrentMarketState(DEFAULT_MARKET);
    setSuggestedMarket(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }, []);

  return (
    <MarketContext.Provider value={{
      currentMarket,
      setCurrentMarket,
      resetToDefault,
      availableMarkets,
      marketsLoaded,
      suggestedMarket,
      dismissSuggestion,
    }}>
      {children}
    </MarketContext.Provider>
  );
}

export function useMarket() {
  const ctx = useContext(MarketContext);
  if (!ctx) throw new Error('useMarket must be used inside <MarketProvider>');
  return ctx;
}
